import type {
  AreaMapArea,
  AreaMapContractor,
  AreaMapKind,
  AreaMapProject,
  AreaMapProjectOrder,
  OrderGroup,
} from "./api";

/** Filtr rodzaju nad listą: zaznaczone rodzaje (firma wchodzi, gdy ma KTÓRYKOLWIEK z nich);
 * pusta lista = wszyscy. */
export type KindFilter = AreaMapKind[];

/** Czy firma przechodzi filtr rodzaju. */
export const matchesKinds = (c: Pick<AreaMapContractor, "kinds">, kinds: KindFilter): boolean =>
  kinds.length === 0 || c.kinds.some((k) => kinds.includes(k));

// NFD does not decompose ł, hence the extra replace (same approach as Sidebar search).
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");
const fold = (s: string) =>
  s.toLocaleLowerCase("pl").normalize("NFD").replace(COMBINING_MARKS, "").replace(/ł/g, "l");

/** Firmy zgodne z filtrem rodzaju i wyszukiwaniem (nazwa, skrót, województwo obszaru,
 * powiat aktywnego zamówienia). */
export function filterContractors(
  rows: AreaMapContractor[],
  kinds: KindFilter,
  query: string,
): AreaMapContractor[] {
  const q = fold(query.trim());
  return rows.filter((c) => {
    if (!matchesKinds(c, kinds)) return false;
    if (!q) return true;
    const orderCounties = Object.values(c.orders).flatMap((g) => g?.counties.map((x) => x.name) ?? []);
    const haystack = [
      c.name,
      c.short_name,
      ...c.areas.map((a) => a.voivodeship_label),
      ...orderCounties,
    ]
      .map(fold)
      .join(" ");
    return haystack.includes(q);
  });
}

/** Firmy pokazane na mapie: zgodne z filtrem rodzaju i nieodznaczone na liście. Szukajka
 * zawęża tylko LISTĘ — wpisanie nazwy nie ma czyścić mapy z reszty firm. */
export function visibleContractorIds(
  rows: AreaMapContractor[],
  kinds: KindFilter,
  excluded: Set<string>,
): Set<string> {
  return new Set(
    filterContractors(rows, kinds, "")
      .filter((c) => !excluded.has(c.id))
      .map((c) => c.id),
  );
}

function areaLabel(area: AreaMapArea): string {
  if (area.whole) return area.voivodeship_label;
  return `${area.voivodeship_label} (${area.counties.length} z ${area.county_total} pow.)`;
}

/** „dolnośląskie, opolskie (3 z 12 pow.)” albo informacja o braku obszaru. */
export function areaSummary(areas: AreaMapArea[]): string {
  if (areas.length === 0) return "brak obszaru działania";
  return areas.map(areaLabel).join(", ");
}

/** Firma w dymku powiatu. */
export interface CountyPresence {
  contractor: AreaMapContractor;
  /** Powiat leży w zadeklarowanym obszarze działania firmy. */
  inArea: boolean;
  /** Aktywne zamówienia firmy w tym powiecie, per grupa (brak klucza = 0). */
  orders: Partial<Record<OrderGroup, number>>;
  /** Ile z nich (łącznie w grupach) firma już przyjęła. */
  accepted: number;
}

/** Kod powiatu → firmy, które mają go w obszarze działania ALBO mają w nim aktywne
 * zamówienie. W każdym powiecie najpierw firmy z obszarem, potem te „poza obszarem”;
 * wewnątrz grupy kolejność listy. Liczone raz na zmianę zbioru pokazanych firm. */
export function countyPresenceIndex(contractors: AreaMapContractor[]): Map<string, CountyPresence[]> {
  const index = new Map<string, Map<string, CountyPresence>>();
  const entry = (code: string, c: AreaMapContractor) => {
    let byContractor = index.get(code);
    if (!byContractor) index.set(code, (byContractor = new Map()));
    let presence = byContractor.get(c.id);
    if (!presence) {
      presence = { contractor: c, inArea: false, orders: {}, accepted: 0 };
      byContractor.set(c.id, presence);
    }
    return presence;
  };
  for (const c of contractors) {
    for (const code of c.county_codes) entry(code, c).inArea = true;
    for (const [group, summary] of Object.entries(c.orders) as [OrderGroup, AreaMapContractor["orders"][OrderGroup]][]) {
      for (const county of summary?.counties ?? []) {
        if (!county.teryt) continue;
        const presence = entry(county.teryt, c);
        presence.orders[group] = county.count;
        presence.accepted += county.accepted;
      }
    }
  }
  return new Map(
    [...index].map(([code, byContractor]) => [
      code,
      [...byContractor.values()].sort((a, b) => Number(b.inArea) - Number(a.inArea)),
    ]),
  );
}

/** Powiaty, w których firma ma PRZYJĘTE zamówienie, choć nie ma ich w obszarze działania
 * (warstwa „Przyjęte zamówienia poza obszarem” i wykrzyknik w dymku). */
export function outsideAcceptedCounties(c: AreaMapContractor): string[] {
  const area = new Set(c.county_codes);
  const out = new Set<string>();
  for (const summary of Object.values(c.orders)) {
    for (const county of summary?.counties ?? []) {
      if (county.teryt && county.accepted > 0 && !area.has(county.teryt)) out.add(county.teryt);
    }
  }
  return [...out].sort();
}

/** Wykrzyknik w dymku: przyjęte zamówienie w powiecie spoza obszaru działania. */
export const isAcceptedOutsideArea = (p: CountyPresence) => !p.inArea && p.accepted > 0;

/** Stan pinezki projektu — filtr podcheckboxami w Warstwach (kolor = firmy, nie stan). */
export type PinStatus = "accepted" | "pending" | "mixed" | "empty";

/** Rodzaj firmy → grupy jej zamówień: przy filtrze „WPW” liczą się tylko zamówienia WPW
 * (geodeta-negocjator nie wnosi do pinezki swojego MDCP), „Geodeta z WPW” zbiera zgody przy
 * okazji MDCP, więc liczą się i mapy, i WPW. Kilka rodzajów naraz = suma ich grup. */
export const KIND_ORDER_GROUPS: Record<AreaMapKind, OrderGroup[]> = {
  wpw: ["wpw"],
  surveyor: ["maps"],
  surveyor_wpw: ["maps", "wpw"],
  coordinator: ["lead"],
};

/** Grupy zamówień liczone do pinezek przy danym filtrze (null = wszystkie). */
export function orderGroupsFor(kinds: KindFilter): Set<OrderGroup> | null {
  if (kinds.length === 0) return null;
  return new Set(kinds.flatMap((k) => KIND_ORDER_GROUPS[k]));
}

export interface ProjectPin {
  project: AreaMapProject;
  /** Zamówienia pokazanych firm w wybranym rodzaju. */
  orders: AreaMapProjectOrder[];
  /** Firmy realizujące te zamówienia — kolejność listy (stałe kolory segmentów). */
  contractors: string[];
  status: PinStatus;
}

/** Pinezki dla pokazanych firm i wybranego rodzaju. Projekt bez ich zamówień zostaje jako
 * „empty” (szary) tylko wtedy, gdy jest aktywny; wstrzymany znika. */
export function projectPins(
  projects: AreaMapProject[],
  visibleIds: Set<string>,
  kinds: KindFilter,
  contractorOrder: Map<string, number>,
): ProjectPin[] {
  const groups = orderGroupsFor(kinds);
  const pins: ProjectPin[] = [];
  for (const project of projects) {
    const orders = project.orders.filter(
      (o) => visibleIds.has(o.contractor) && (groups === null || groups.has(o.group)),
    );
    if (orders.length === 0) {
      if (project.active) pins.push({ project, orders, contractors: [], status: "empty" });
      continue;
    }
    const contractors = [...new Set(orders.map((o) => o.contractor))].sort(
      (a, b) => (contractorOrder.get(a) ?? 0) - (contractorOrder.get(b) ?? 0),
    );
    const accepted = orders.some((o) => o.accepted);
    const pending = orders.some((o) => !o.accepted);
    const status = accepted && pending ? "mixed" : accepted ? "accepted" : "pending";
    pins.push({ project, orders, contractors, status });
  }
  return pins;
}
